import { useEffect, useState } from "react";
import { Link } from "react-router";
import type { Fonte, ItemEmExecucao } from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";
import { Icone } from "../componentes/Icone.js";
import { IconeDuvida } from "../componentes/icones.js";

/**
 * "Não entendi" — RN-08, e o que fecha o ciclo do SPEC §1.
 *
 * É **distinto de "errei"**: registra lacuna, não desempenho. Não mexe na fila
 * de erradas, não conta como resposta e não entra no diário.
 *
 * E entrega as três coisas de uma vez, porque qualquer uma sozinha o
 * transformaria num marcador: a **dúvida** fica registrada, a **fonte da
 * categoria** aparece aqui mesmo — subindo na árvore se a categoria específica
 * não tiver nenhuma — e o **esboço de nota** já sai pronto com o item anexado,
 * a um toque de distância. Além disso, os itens dessa categoria passam a vir
 * primeiro na próxima montagem de sessão.
 */
export function NaoEntendi({ item, aoFechar }: { item: ItemEmExecucao; aoFechar: () => void }) {
  const [descricao, setDescricao] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [resultado, setResultado] = useState<{
    fontes: Fonte[];
    herdadasDe: string[] | null;
    esboco: { titulo: string; html: string; categoriaId: string | null; itensAnexados: string[] };
  } | null>(null);

  useEffect(() => {
    setDescricao("");
    setResultado(null);
    setErro(null);
  }, [item.id]);

  async function registrar() {
    setEnviando(true);
    setErro(null);
    try {
      const r = await pedir("estudo.naoEntendi", { corpo: { itemId: item.id, descricao } });
      setResultado({ fontes: r.fontes, herdadasDe: r.fontesHerdadasDe, esboco: r.esboco });
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível registrar a dúvida.");
    } finally {
      setEnviando(false);
    }
  }

  if (resultado) {
    const busca = new URLSearchParams({
      titulo: resultado.esboco.titulo,
      html: resultado.esboco.html,
    });
    if (resultado.esboco.categoriaId) busca.set("categoria", resultado.esboco.categoriaId);
    for (const i of resultado.esboco.itensAnexados) busca.append("item", i);

    return (
      <section className="cartao pilha duvida-registrada" aria-live="polite">
        <p className="etiqueta">Dúvida registrada</p>
        <p className="estado__texto">
          Os itens de <strong>{item.categoria.join(" › ")}</strong> vão vir primeiro na próxima sessão, até você
          resolver esta dúvida.
        </p>

        <h3 className="etiqueta">
          {resultado.fontes.length > 0 ? "Onde estudar isso" : "Nenhuma fonte cadastrada ainda"}
        </h3>
        {resultado.herdadasDe && (
          <p className="campo__ajuda">Fontes de {resultado.herdadasDe.join(" › ")}, que cobre esta categoria.</p>
        )}
        {resultado.fontes.length > 0 ? (
          <ul className="pilha">
            {resultado.fontes.map((f) => (
              <li key={f.id}>
                <p className="trilha__nome">{f.titulo}</p>
                {f.nota && <p className="campo__ajuda">{f.nota}</p>}
                {f.url && (
                  <a className="botao botao--texto" href={f.url} target="_blank" rel="noopener noreferrer">
                    Abrir a fonte
                  </a>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="leitura--apoio">
            Cadastre onde você estuda este assunto e ela aparece aqui na próxima vez.
          </p>
        )}

        <div className="linha trilha__acoes">
          <Link className="botao botao--compacto botao--estreito" to={`/caderno/nova?${busca.toString()}`}>
            Escrever a nota
          </Link>
          <Link className="botao botao--secundario botao--compacto botao--estreito" to="/fontes">
            {resultado.fontes.length > 0 ? "Ver fontes" : "Cadastrar fonte"}
          </Link>
        </div>
      </section>
    );
  }

  // Quem abre é o "não entendi" da barra; aqui não há um segundo botão para
  // abrir de novo o que já foi aberto.
  return (
    <section className="cartao pilha">
      <p className="etiqueta">
        <Icone glifo={IconeDuvida} papel="estado" tamanho="p" />
        Não entendi
      </p>
      <p className="campo__ajuda">
        Isto não conta como erro — registra que ficou uma lacuna, e é diferente de ter errado a questão.
      </p>
      <div className="campo">
        <label className="campo__rotulo" htmlFor="descricao-duvida">
          O que ficou confuso? <span className="tinta-suave">(opcional)</span>
        </label>
        <textarea
          id="descricao-duvida"
          className="campo__entrada campo__area"
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          rows={2}
        />
      </div>
      {erro && (
        <p className="campo__erro" role="alert">
          {erro}
        </p>
      )}
      <div className="linha">
        <button className="botao botao--secundario" type="button" onClick={aoFechar} disabled={enviando}>
          Cancelar
        </button>
        <button className="botao" type="button" onClick={() => void registrar()} disabled={enviando}>
          {enviando ? "Registrando…" : "Registrar dúvida"}
        </button>
      </div>
    </section>
  );
}
